<template>
	<div
		class="split-container"
		:class="{ 'split-container--horizontal': direction === 'horizontal' }"
		data-testid="vue-split"
	>
		<slot />
	</div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import Split from "split.js";
import type { VueSplitExpose, VueSplitProps } from "./vue-split.types";

interface SplitInstance {
	destroy(): void;
	getSizes(): number[];
	collapse(index: number): void;
	setSizes(sizes: number[]): void;
}

const props = withDefaults(defineProps<VueSplitProps>(), {
	direction: "horizontal",
	sizes: null,
	minSize: 100,
	gutterSize: 5,
	snapOffset: 30,
	cursor: "grabbing"
});

const emit = defineEmits<{
	(e: "onDrag", payload: { sizes: number[] }): void;
	(e: "onDragEnd", payload: { sizes: number[] }): void;
	(e: "onDragStart", payload: { sizes: number[] }): void;
}>();

const split = ref<SplitInstance | null>(null);

function validateElements(elements: string[]): void {
	const isValid = elements.every((item) => typeof item === "string");

	if (!isValid) {
		throw new Error(
			`VueSplitJs: Invalid elements - "${elements}". Must be array of strings`
		);
	}
}

function validateDirection(direction: string): void {
	const allowedValues = ["horizontal", "vertical"];
	const isValid = allowedValues.includes(direction);

	if (!isValid) {
		throw new Error(
			`VueSplitJs: Invalid direction - "${direction}". Possible values are "horizontal" or "vertical"`
		);
	}
}

function validateSizes(sizes: number[] | null | undefined): void {
	if (!sizes) {
		return;
	}

	const isValid = sizes.every((item) => typeof item === "number");

	if (!isValid) {
		throw new Error(
			`VueSplitJs: Invalid sizes - "${sizes}". Must be array of numbers`
		);
	}
}

function destroySplit(): void {
	split.value?.destroy();
	split.value = null;
}

function emitDrag(): void {
	emit("onDrag", { sizes: split.value?.getSizes() ?? [] });
}

function emitDragStart(): void {
	emit("onDragStart", { sizes: split.value?.getSizes() ?? [] });
}

function emitDragEnd(): void {
	emit("onDragEnd", { sizes: split.value?.getSizes() ?? [] });
}

function createSplit(): void {
	validateElements(props.elements);
	validateDirection(props.direction);
	validateSizes(props.sizes);

	const splitOptions: Split.Options = {
		direction: props.direction,
		...(props.sizes ? { sizes: props.sizes } : {}),
		minSize: props.minSize,
		gutterSize: props.gutterSize,
		snapOffset: props.snapOffset,
		cursor: props.cursor,
		onDrag: emitDrag,
		onDragStart: emitDragStart,
		onDragEnd: emitDragEnd
	};

	split.value = Split(props.elements, splitOptions) as SplitInstance;
}

function rebuildSplit(): void {
	destroySplit();
	createSplit();
}

function collapse(index: number): void {
	split.value?.collapse(index);
}

function expand(): void {
	if (!split.value || !props.sizes) {
		return;
	}

	split.value.setSizes(props.sizes);
}

defineExpose<VueSplitExpose>({
	collapse,
	expand
});

onMounted(() => {
	createSplit();
});

onBeforeUnmount(() => {
	destroySplit();
});

watch(
	[() => props.elements, () => props.gutterSize, () => props.direction],
	() => {
		rebuildSplit();
	},
	{ deep: true }
);
</script>

<style lang="scss">
$color-screen-splitter-background: #eee;

.split-container {
	height: 100%;
}

.split-container > * {
	position: relative;
	overflow-y: hidden;
}

.split-container--horizontal {
	display: flex;
}

.gutter {
	background-color: $color-screen-splitter-background;
	background-repeat: no-repeat;
	background-position: 50%;
}

.gutter.gutter-vertical {
	background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAB4AAAAFAQMAAABo7865AAAABlBMVEVHcEzMzMzyAv2sAAAAAXRSTlMAQObYZgAAABBJREFUeF5jOAMEEAIEEFwAn3kMwcB6I2AAAAAASUVORK5CYII=");
	cursor: ns-resize;
}

.gutter.gutter-horizontal {
	float: left;
	cursor: ew-resize;
	background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAeCAYAAADkftS9AAAAIklEQVQoU2M4c+bMfxAGAgYYmwGrIIiDjrELjpo5aiZeMwF+yNnOs5KSvgAAAABJRU5ErkJggg==");
}

.split {
	-webkit-box-sizing: border-box;
	-moz-box-sizing: border-box;
	box-sizing: border-box;
	overflow-y: auto;
	overflow-x: hidden;
}
</style>
